import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Heart, Home, LayoutGrid, Menu, Search, ShoppingCart, Timer, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useShop, useSettings } from "@/lib/shop";

export const NAV = [
  { to: "/", label: "Beranda", id: "beranda" },
  { to: "/katalog", label: "Katalog", id: "katalog" },
  { to: "/kategori", label: "Kategori", id: "kategori" },
  { to: "/pre-order", label: "Pre-Order", id: "pre-order" },
  { to: "/promo", label: "Promo", id: "promo" },
  { to: "/jual-koleksi", label: "Jual Koleksi Kamu", id: "jual-koleksi" },
  { to: "/tentang", label: "Tentang Kami", id: "tentang" },
];

function SearchBox({ onDone, testid }) {
  const [q, setQ] = useState("");
  const nav = useNavigate();
  const submit = (e) => {
    e.preventDefault();
    nav(`/katalog?q=${encodeURIComponent(q.trim())}`);
    onDone?.();
  };
  return (
    <form onSubmit={submit} className="relative w-full" role="search">
      <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
      <input
        data-testid={testid}
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Cari Gunpla, figure, SKU, merek..."
        className="w-full h-11 rounded-full border-2 border-neutral-900 bg-white pl-10 pr-4 text-sm outline-none focus:ring-4 focus:ring-[var(--bk-yellow)]/60"
      />
    </form>
  );
}

const IconBtn = ({ to, icon: Icon, count, label, testid }) => (
  <Link to={to} aria-label={label} data-testid={testid} className="relative h-11 w-11 grid place-items-center rounded-full border-2 border-neutral-900 bg-white hover:bg-[var(--bk-yellow)] transition-colors">
    <Icon className="h-5 w-5" />
    {count > 0 && (
      <span data-testid={`${testid}-count`} className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 rounded-full bg-[var(--bk-red)] text-white text-[11px] font-bold grid place-items-center border-2 border-white">
        {count}
      </span>
    )}
  </Link>
);

export default function Header() {
  const { cartCount, favs } = useShop();
  const s = useSettings();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const linkCls = ({ isActive }) =>
    `px-3 py-2 rounded-full text-[14.5px] font-semibold transition-colors ${isActive ? "bg-neutral-900 text-white" : "hover:bg-[var(--bk-yellow)]"}`;
  return (
    <header className="sticky top-0 z-50">
      <div className="bg-neutral-900 text-white text-xs">
        <div className="container-x flex items-center justify-between h-8">
          <span className="truncate">
            <span className="text-[var(--bk-yellow)] font-bold">Pontianak</span> · {s.address?.split(",")[0]}
          </span>
          <a href={`https://wa.me/${s.whatsapp}`} target="_blank" rel="noreferrer" data-testid="topbar-wa-link" className="hidden sm:inline font-semibold hover:text-[var(--bk-yellow)]">
            WhatsApp {s.whatsapp_display || s.whatsapp}
          </a>
        </div>
      </div>
      <div className="bg-white/95 backdrop-blur-md border-b-2 border-neutral-900">
        <div className="container-x flex items-center gap-3 h-[72px]">
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="mobile-menu-button" aria-label="Menu" className="lg:hidden h-11 w-11 grid place-items-center rounded-full border-2 border-neutral-900">
                <Menu className="h-5 w-5" />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="w-[300px] p-0">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="p-5 border-b bg-[var(--bk-yellow)]">
                <img src="/brand/logo-sm.webp" alt="Bengkel Toys & Hobbies" className="h-16 w-auto" />
              </div>
              <nav className="p-3 flex flex-col">
                {[...NAV, { to: "/koleksi-titipan", label: "Koleksi Titipan", id: "koleksi-titipan" }, { to: "/panduan", label: "Panduan Pemesanan", id: "panduan" }, { to: "/kontak", label: "Kontak & Lokasi", id: "kontak" }, { to: "/favorit", label: "Favorit", id: "favorit" }].map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.to === "/"} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.id}`} className={({ isActive }) => `px-4 py-3 rounded-lg font-semibold ${isActive ? "bg-neutral-900 text-white" : "hover:bg-neutral-100"}`}>
                    {n.label}
                  </NavLink>
                ))}
              </nav>
            </SheetContent>
          </Sheet>
          <Link to="/" data-testid="header-logo-link" className="shrink-0">
            <img src="/brand/logo-sm.webp" alt="Bengkel Toys & Hobbies" className="h-14 w-auto" width="60" height="56" />
          </Link>
          <nav className="hidden lg:flex items-center gap-0.5 ml-2">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.to === "/"} className={linkCls} data-testid={`nav-${n.id}-link`}>
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="hidden md:block flex-1 max-w-xs ml-auto">
            <SearchBox testid="header-search-input" />
          </div>
          <div className="flex items-center gap-2 ml-auto md:ml-0">
            <button data-testid="mobile-search-toggle" aria-label="Cari" onClick={() => setSearchOpen((v) => !v)} className="md:hidden h-11 w-11 grid place-items-center rounded-full border-2 border-neutral-900">
              {searchOpen ? <X className="h-5 w-5" /> : <Search className="h-5 w-5" />}
            </button>
            <IconBtn to="/favorit" icon={Heart} count={favs.length} label="Favorit" testid="header-fav-link" />
            <IconBtn to="/keranjang" icon={ShoppingCart} count={cartCount} label="Keranjang" testid="header-cart-link" />
          </div>
        </div>
        {searchOpen && (
          <div className="md:hidden container-x pb-3">
            <SearchBox testid="mobile-search-input" onDone={() => setSearchOpen(false)} />
          </div>
        )}
      </div>
    </header>
  );
}

export function MobileTabBar() {
  const { cartCount } = useShop();
  const items = [
    { to: "/", icon: Home, label: "Beranda", id: "home" },
    { to: "/katalog", icon: LayoutGrid, label: "Katalog", id: "katalog" },
    { to: "/pre-order", icon: Timer, label: "Pre-Order", id: "po" },
    { to: "/favorit", icon: Heart, label: "Favorit", id: "fav" },
    { to: "/keranjang", icon: ShoppingCart, label: "Keranjang", id: "cart", count: cartCount },
  ];
  return (
    <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t-2 border-neutral-900 grid grid-cols-5 pb-[env(safe-area-inset-bottom)]">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.to === "/"} data-testid={`tabbar-${it.id}`} className={({ isActive }) => `relative flex flex-col items-center justify-center h-16 text-[11px] font-semibold ${isActive ? "text-neutral-900" : "text-neutral-500"}`}>
          {({ isActive }) => (
            <>
              <span className={`h-8 w-12 grid place-items-center rounded-full ${isActive ? "bg-[var(--bk-yellow)]" : ""}`}>
                <it.icon className="h-5 w-5" />
              </span>
              {it.label}
              {it.count > 0 && <span className="absolute top-1.5 right-[22%] min-w-[18px] h-[18px] px-1 rounded-full bg-[var(--bk-red)] text-white text-[10px] font-bold grid place-items-center">{it.count}</span>}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}
